/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { isMobile } from 'react-device-detect'
import useRootStore from '@store/useRootStore'
import { SPORT_ID } from 'adv-protocol/src/GameCommon'
import { isLsportsEnv, makeSimpleMatchInfoTrackByDetailTrack } from '@utils/utils'
import { GAME_TYPE } from '@game-type/game-market-types'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import { FROM_VIEW_TYPE } from '@v2/constants/common'
import { SimpleMatchInfo } from '@views/Matchup/MatchUpDetail/SimpleMatchInfo'
import { SportTalk } from '@views/Matchup/MatchUpDetail/TabContent/SportTalk'
import { MatchUpDetailSportTalkWrap } from '@views/Matchup/MatchUpDetail/index.style'
import { ContentWrap } from '@styles/base.style'
import { runInAction } from 'mobx'
import { PAGE_TYPE } from '@interface/common'
import dayjs from 'dayjs'
import { BettingFoldData, BettingFoldGrouped, BettingRecordTrack } from '@store/bettingHistoryStore'

const BETRADAR_CLASS = 'betradar'
const STATSCORE_CLASS = 'statscore'

interface SportTalkPopupProps {
  matchId: number,
  isMatchClosed?: boolean
  fromViewType?: FROM_VIEW_TYPE,
  cheerBettingRecord?: CheerBettingRecord,
}

export interface CheerBettingRecord {
  record: BettingRecordTrack,
  fold: BettingFoldGrouped,
  foldData: BettingFoldData
}

const SportTalkPopup: React.FC<SportTalkPopupProps> = (props) => {
  const {
    matchId,
    isMatchClosed = false,
    cheerBettingRecord,
    fromViewType = FROM_VIEW_TYPE.SPORT_TALK
  } = props
  const { matchupStore, globalStore, cheerForTeamStore, bettingHistoryStore } = useRootStore()
  const {
    sportTalkPopupMatchTrack,
    sportTalkPopupList,
    hasMoreSportTalkPopupList,
  } = cheerForTeamStore
  const [matchDetailPackage, setMatchDetailPackage] = useState(null)
  const [simpleMatchInfoClassName, setSimpleMatchInfoClassName] = useState('')
  const [liveScoreIframeWrapClassName, setLiveScoreIframeWrapClassName] = useState('')

  const currentComponentRef = useRef<HTMLDivElement>(null)
  const scrollElementRef = useRef(null)
  const simpleMatchInfoIntervalIdRef = useRef(null)

  const isNotStarted = dayjs().isBefore(sportTalkPopupMatchTrack.dateOfMatch)
  const isLive = sportTalkPopupMatchTrack.liveOdds

  // visualization 설정
  let visualSupport = false
  switch (sportTalkPopupMatchTrack.sportId) {
    case SPORT_ID.SOCCER:
    case SPORT_ID.BASKETBALL:
    case SPORT_ID.ICE_HOCKEY:
    case SPORT_ID.TABLE_TENNIS:
      visualSupport = true
      break
    case SPORT_ID.BASEBALL:
      if (sportTalkPopupMatchTrack.categoryId === 16 && sportTalkPopupMatchTrack.tournamentId === 109) {
        visualSupport = true
      }
      break
  }
  if (isLsportsEnv()) {
    visualSupport = true
  }

  useOnMount(async () => {
    scrollElementRef.current = currentComponentRef.current.parentNode

    scrollElementRef.current.addEventListener('scroll', scrollHandler, true)
    if (isLive) {
      scrollElementRef.current.addEventListener('scroll', liveMatchScrollHandler, true)
    }

    if (sportTalkPopupMatchTrack?.matchId !== matchId) {
      cheerForTeamStore.fetchSportTalkSimpleMatchInfoTrack(matchId).catch(console.error)
      return
    }

    cheerForTeamStore.fetchSportTalkDetailPackage({ matchId, sportId: sportTalkPopupMatchTrack.sportId }).then(res => {
      setMatchDetailPackage(res)
    })

    if (simpleMatchInfoIntervalIdRef.current) {
      clearInterval(simpleMatchInfoIntervalIdRef.current)
      simpleMatchInfoIntervalIdRef.current = null
    }

    if (!isMatchClosed && !visualSupport && isLive) {
      simpleMatchInfoIntervalIdRef.current = setInterval(() => {
        if (!isMobile && matchupStore.detailMatchId === matchId) {
          const track = makeSimpleMatchInfoTrackByDetailTrack(matchupStore.detailTrack)
          cheerForTeamStore.setSimpleMatchInfo(track)
        } else {
          cheerForTeamStore.fetchSportTalkSimpleMatchInfoTrack(matchId).catch(console.error)
        }
      }, 10000)
    }
  })

  useOnUnmount(() => {
    if (scrollElementRef.current) {
      scrollElementRef.current.removeEventListener('scroll', scrollHandler, true) /* 이벤트 삭제 */
      scrollElementRef.current.removeEventListener('scroll', liveMatchScrollHandler, true)
      scrollElementRef.current = null
    }

    if (simpleMatchInfoIntervalIdRef.current) {
      clearInterval(simpleMatchInfoIntervalIdRef.current)
      simpleMatchInfoIntervalIdRef.current = null
    }

    if (cheerBettingRecord) {
      bettingHistoryStore.resetSelectedBettingHistory(FROM_VIEW_TYPE.SPORT_TALK)
    }

    currentComponentRef.current = null
    cheerForTeamStore.resetFetchSportTalkPopupPagination()

    runInAction(() => {
      cheerForTeamStore.sportTalkPopupList = []
    })
  })

  const scrollHandler = () => {
    if (isMobile && globalStore.currentGeneralPopup) {
      const scrollPosition = currentComponentRef.current.scrollTop
      if (visualSupport) {
        // 비주얼라이제이션 지원 O
        const visualSection = currentComponentRef.current.querySelector('#betIframeWrap')
        const visualH = visualSection?.scrollHeight || 0

        if (isMatchClosed || !isLive) {
          if (scrollPosition > visualH - 150 && scrollPosition > 150) {
            setSimpleMatchInfoClassName('show-simple-info')
          } else {
            setSimpleMatchInfoClassName('')
          }
        } else {
          // 라이브일 경우 iframe 띄움
          if (scrollPosition > visualH - 150 && scrollPosition > 150) {
            setLiveScoreIframeWrapClassName('show-live-score')
          } else {
            setLiveScoreIframeWrapClassName('')
          }
        }
      } else {
        // 비주얼라이제이션 지원 X
        const previewSection = currentComponentRef.current.querySelector('#gamePreviewWrap')
        const previewH = previewSection.scrollHeight

        if (scrollPosition > previewH - 35 && scrollPosition > 40) {
          setSimpleMatchInfoClassName('show-simple-info')
        } else {
          setSimpleMatchInfoClassName('')
        }
      }
    }
  }

  const liveMatchScrollHandler = () => {
    const scrollPosition = currentComponentRef.current.scrollTop
    const liveDetailTab = currentComponentRef.current.querySelector('#detailTabWrap')
    if (scrollPosition > 10 && liveDetailTab) {
      liveDetailTab.classList.add('scroll')
    }
  }

  const fetchSportTalk = async (matchId: number, reset = false) => {
    const timelineEnabled = globalStore.getSportTalkMatchTimelineEnabled()
    await cheerForTeamStore.fetchSportTalkListInPopup(matchId, reset, !timelineEnabled)
  }

  let iframeWrapTypeClassName
  if (isLsportsEnv()) {
    iframeWrapTypeClassName = STATSCORE_CLASS
  } else {
    iframeWrapTypeClassName = BETRADAR_CLASS
  }

  let contentWarpStyle
  if (!isMobile) {
    contentWarpStyle = { overflow: 'auto' }
  }

  let gameKey: GAME_TYPE
  let pageType: PAGE_TYPE
  if (isMatchClosed) {
    pageType = PAGE_TYPE.MATCHUP_RESULT
    gameKey = GAME_TYPE.CROSS
  } else if (!isLive || (!visualSupport && isNotStarted)) {
    pageType = PAGE_TYPE.SPORT
    gameKey = GAME_TYPE.CROSS
  } else {
    pageType = PAGE_TYPE.LIVE
    gameKey = GAME_TYPE.LIVE
  }

  return (
    <ContentWrap
      ref={currentComponentRef}
      style={contentWarpStyle}
      className="content-wrap no-padding no-bg detail-bet">
      <div className={isMobile ? 'detail-bet-delay' : 'detail-bet-delay pc'}>
        {sportTalkPopupMatchTrack &&
          <SimpleMatchInfo
            track={sportTalkPopupMatchTrack}
            outcomes={matchDetailPackage?.outcomes}
            wayRatio={matchDetailPackage?.wayRatio}
            visualSupport={visualSupport}
            gameKey={gameKey}
            pageType={pageType}
            liveScoreIframeWrapClassName={liveScoreIframeWrapClassName}
            simpleMatchInfoClassName={simpleMatchInfoClassName}
            iframeWrapTypeClassName={`${iframeWrapTypeClassName} sport-talk-popup`}
          />
        }
        <div className="detail-bet-contents-wrap">
          <div className="render-contents-wrap">
            <MatchUpDetailSportTalkWrap>
              <SportTalk
                track={sportTalkPopupMatchTrack}
                sportTalkItems={sportTalkPopupList}
                hasMoreSportTalk={hasMoreSportTalkPopupList}
                fetchSportTalkHandler={fetchSportTalk}
                fromViewType={fromViewType}
                contentWrapRef={scrollElementRef}
                isInitBettingShare={true}
              />
            </MatchUpDetailSportTalkWrap>
          </div>
        </div>
      </div>
    </ContentWrap>
  )
}

export default observer(SportTalkPopup)
